import { useState } from 'react'
import { useRouter } from 'next/router'
import { Row, Col, Button } from 'antd'
import { useSelector } from 'react-redux'
import useTranslation from '@/hooks/useTranslation'
import { CAF_REQUEST_STATUS } from '@/constants/CAFePatronato.constants'

import Loading from '@/components/shared-components/Loading'
import { AppTableStatus } from '@/components/shared-components'
import Calendar from '@/components/svgs/formBuild/Calendar'
import Euro from '@/components/svgs/Euro'
import Attachment from '@/components/svgs/Attachment'
import UploadMissingInfo from '@/components/shared-components/modals/request/UploadMissingInfo'
import Write from '@/components/svgs/buttonIcons/Write'
import Upload from '@/components/svgs/Upload'
import Refresh from '@/components/svgs/auth/Refresh'
import Arrowright from '@/components/svgs/buttonIcons/ArrowRight'

const RequestCard = ({ request }) => {
  const { t } = useTranslation()
  const { push } = useRouter()

  const { isCompleted } = useSelector((state) => state.auth)

  const [currentId, setCurrentId] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [notes, setNotes] = useState(null)

  const request_status = CAF_REQUEST_STATUS.find(
    (ele) => ele.value === request.status
  )

  const isWithoutFormService = Number(request.service?.without_form) === 1
  const isProfileIncomplete = !isCompleted
  const isDraftOrPendingStatus = request.status === 12 || request.status === 1
  const shouldShowCompleteModuleButton =
    isWithoutFormService &&
    isProfileIncomplete &&
    isDraftOrPendingStatus &&
    !request.reseller_id
  const shouldShowDraftFormButton =
    request.status === 12 &&
    request.new_form === 1 &&
    !request.reseller_id &&
    !isWithoutFormService
  const canShowEditButton =
    request.status !== 12 && request.status === 1 && !isWithoutFormService

  const transmissionFilesCount = request.transmission_files.length || 0
  const completeFilesCount = request.complete_files.length || 0
  const missingFilesCount = request.missing_files?.length || 0

  const totalFilesCount =
    transmissionFilesCount + completeFilesCount + missingFilesCount

  return (
    <div
      className='shadow-lg rounded-xl p-24'
      onClick={() => {
        if (uploadOpen) return
         push(`requests/${request.id}`)
      }}
    >
      <div className='mb-12'>
        <h4 className='text-xl font-semibold mb-8'>{request.title}</h4>
        <div className='text-dimmed text-sm flex items-center gap-8'>
          <span>#{request.id_pratica}</span>
          <span className='flex items-center gap-4'>
            <Calendar stroke='#6D6D6D' width='14' height='14' />{' '}
            {request.request_at?.substr(0, 10).split('-').reverse().join('/')}
          </span>
        </div>
      </div>

      <div className='mb-16'>
        <p className='text-green text-lg font-bold flex items-center'>
          {Number(request.cost).toFixed(2).replace('.', ',')}{' '}
          <Euro color='#8FC045' width='16' height='16' />
        </p>
      </div>

      <div className='flex items-center gap-12'>
        {totalFilesCount > 0 && (
          <div className='bg-[#F7F7F7] text-base font-medium rounded-full py-8 px-16 flex items-center justify-center gap-4 w-fit'>
            <Attachment width='16' height='16' />
            Documenti
            <span className='w-[24px] h-[24px] text-base font-semibold flex items-center justify-center rounded-full bg-black text-white'>
              {totalFilesCount}
            </span>
          </div>
        )}
        <AppTableStatus
          withDot
          text={t(request_status?.label)}
          color={request_status?.color}
          className='!text-base !rounded-full !py-8'
        />
      </div>

      {shouldShowCompleteModuleButton && (
        <Button
          className='btn-green font-semibold py-8 rounded-full w-full h-auto mt-16'
          onClick={(e) => {
            e.stopPropagation()
            push(`/dashboard/requests/${request.id}/edit`)
          }}
        >
          <Arrowright stroke='#FFF' />
          {t('Completa modulo')}
        </Button>
      )}

      {shouldShowDraftFormButton && (
          <Button
            className='btn-green font-semibold py-8 rounded-full w-full h-auto mt-16'
            onClick={(e) => {
              e.stopPropagation()
              if (request.service.is_prepaid === 1) {
                push(`/dashboard/servizi/${request.service.slug}/pay`)
              } else {
                push(`/dashboard/servizi/${request.service.slug}/request`)
              }
            }}
          >
            <Arrowright stroke='#FFF' />
            {t('Completa modulo')}
          </Button>
        )}

      {request.status === 4 && request.service.is_isee === 1 && (
        <Button
          className='btn-green font-semibold py-8 rounded-full w-full h-auto mt-16'
          onClick={(e) => {
            e.stopPropagation()
            if (request.service.is_prepaid === 1) {
              push(
                `/dashboard/servizi/${request.service.slug}/pay?action=renew`
              )
            } else {
              push(
                `/dashboard/servizi/${request.service.slug}/request?action=renew`
              )
            }
          }}
        >
          <Refresh stroke='#FFF' width='14' height='14' />
          {t('Renew')}
        </Button>
      )}

      {request.status === 2 && (
        <Button
          className='btn-green font-semibold py-8 rounded-full w-full h-auto mt-16'
          onClick={(e) => {
            e.stopPropagation()
            setCurrentId(request.id)
            setNotes(request.required_missing_info)
            setUploadOpen(true)
          }}
        >
          <Upload color='#FFF' width='14' height='14' />
          {t('button.UploadInfo')}
        </Button>
      )}

      {canShowEditButton && (
        <Button
          className='btn-green font-semibold py-8 rounded-full w-full h-auto mt-16'
          onClick={(e) => {
            e.stopPropagation()
            push(`/dashboard/requests/${request.id}/edit`)
          }}
        >
          <Write color='#FFF' />
          {t('Edit')}
        </Button>
      )}

      <UploadMissingInfo
        open={uploadOpen}
        setOpen={setUploadOpen}
        id={currentId}
        notes={notes}
      />
    </div>
  )
}

const RequestsCards = ({ loading, requests }) => {
  if (loading) return <Loading />

  return (
    <Row gutter={[16, 16]}>
      {requests.map((request, i) => (
        <Col key={i} xs={24}>
          <RequestCard request={request} />
        </Col>
      ))}
    </Row>
  )
}

export default RequestsCards
